@if (classes$ | async; as classes) {
  <div class="mx-auto max-w-2xl">
    @if (!originIsAdminComponent) {
      <!-- Not brand-coloured: on the step before this one, teal means "this day can be
           booked". A heading in the same teal that means nothing dilutes the signal. -->
      <h2 class="mb-4 text-center text-xl font-bold text-fg">
        {{ selectedDate$ | async | date: 'EEEE d MMMM' }}
      </h2>
    }

    <ul role="list" class="space-y-3">
      @for (spinClass of classes; track spinClass.id) {
        <li>
          <app-card>
            <div class="flex flex-wrap items-start justify-between gap-4">
              <div class="min-w-0">
                @if (originIsAdminComponent) {
                  <p class="text-sm text-fg-muted">
                    {{ spinClass.dateEpoch | date: 'dd/MM/yyyy' }}
                  </p>
                }
                <div class="flex flex-wrap items-center gap-2">
                  <span class="text-2xl font-bold text-fg">
                    {{ spinClass.dateEpoch | date: 'HH:mm' }}
                  </span>
                  <!-- The type was missing from the member's card entirely, and it is
                       the only thing separating two classes at the same hour. -->
                  <app-badge [tone]="typeTone(spinClass.type)">
                    {{ spinClass.type }}
                  </app-badge>
                  @if (isFull(spinClass)) {
                    <app-badge tone="danger">Volzet</app-badge>
                  }
                  @if (spinClass.currentReservationsForUser > 0) {
                    <app-badge tone="success">Gereserveerd</app-badge>
                  }
                </div>

                @if (spinClass.currentReservationsForUser > 0) {
                  <p class="mt-1 text-sm text-fg-muted">
                    <span class="font-semibold text-brand">
                      {{ spinClass.currentReservationsForUser
                      }}{{ isAdmin ? '' : ' / 5' }}
                    </span>
                    gereserveerd
                  </p>
                }

                <p class="mt-1 text-sm text-fg-muted">
                  <span
                    class="font-semibold"
                    [class]="availabilityClass(spinClass)"
                  >
                    {{
                      spinClass.maxReservations - spinClass.currentReservations
                    }}
                  </span>
                  van {{ spinClass.maxReservations }} fietsen vrij
                </p>
                <!-- How full, at a glance and in the same colour as the count, so a
                     member scanning several classes can see which are filling up
                     without reading a number on each. -->
                <div
                  class="mt-2 h-1.5 w-full max-w-56 overflow-hidden rounded-full bg-surface-raised"
                  role="progressbar"
                  [attr.aria-valuenow]="fullPercent(spinClass)"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  [attr.aria-label]="'Bezetting van deze les'"
                >
                  <div
                    class="h-full rounded-full transition-[width]"
                    [class]="availabilityFill(spinClass)"
                    [style.width.%]="fullPercent(spinClass)"
                  ></div>
                </div>
              </div>

              <div class="flex shrink-0 flex-col items-stretch gap-2">
                <button
                  appButton
                  size="sm"
                  type="button"
                  [disabled]="
                    reserveDisabledReason(spinClass) !== '' || inSubmission
                  "
                  (click)="
                    addReservation(
                      spinClass,
                      spinClass.currentReservations + 1 >
                        spinClass.maxReservations
                    )
                  "
                >
                  <app-icon name="plus" size="size-4" />
                  Reserveer
                </button>

                @if (spinClass.currentReservationsForUser > 0) {
                  <!-- Secondary, not danger: "Reserveer" is this card's primary action
                       and a red button beside it wins the eye for the wrong one. The
                       red belongs on the confirm step, where cancelling IS the action. -->
                  <button
                    appButton
                    variant="secondary"
                    size="sm"
                    type="button"
                    [disabled]="inSubmission"
                    (click)="removeReservation(spinClass, inSubmission)"
                  >
                    <app-icon name="minus" size="size-4" />
                    Fiets annuleren
                  </button>
                }

                <!-- Suppressed when the class is full: the "Volzet" badge next to the
                     time already says it, and showing both read as a glitch. -->
                @if (
                  !isFull(spinClass) && reserveDisabledReason(spinClass);
                  as reason
                ) {
                  <span class="text-right text-xs text-fg-subtle">{{
                    reason
                  }}</span>
                }
              </div>
            </div>
          </app-card>
        </li>
      } @empty {
        <li>
          <app-card>
            <div class="py-6 text-center">
              <app-icon
                name="calendar"
                size="size-8"
                class="mx-auto block text-fg-subtle"
              />
              <p class="mt-3 font-semibold text-fg">
                Geen lessen op deze datum
              </p>
              @if (!originIsAdminComponent) {
                <button
                  appButton
                  size="sm"
                  type="button"
                  class="mt-4"
                  (click)="navigateStepBack()"
                >
                  <app-icon name="arrow-left" size="size-4" />
                  Andere datum kiezen
                </button>
              }
            </div>
          </app-card>
        </li>
      }
    </ul>

    <div class="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-between">
      @if (!originIsAdminComponent) {
        <button
          appButton
          variant="secondary"
          size="sm"
          type="button"
          (click)="navigateStepBack()"
        >
          <app-icon name="arrow-left" size="size-4" />
          Andere datum kiezen
        </button>
        <button
          appButton
          variant="secondary"
          size="sm"
          type="button"
          (click)="navigateToMyReservations()"
        >
          <app-icon name="calendar" size="size-4" />
          Mijn reservaties
        </button>
      }
      @if ((isAdmin$ | async) && originIsAdminComponent) {
        <button
          appButton
          variant="secondary"
          size="sm"
          type="button"
          [disabled]="inSubmission"
          (click)="toggleUpcoming()"
        >
          <app-icon name="clock" size="size-4" />
          {{ upcomingClasses ? 'Filter op datum' : 'Filter eerstvolgende' }}
        </button>
      }
    </div>
  </div>
} @else {
  <div class="flex justify-center py-8"><app-spinner /></div>
}
